import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Table, Button } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';

import { NO_RESULTS_MESSAGE } from './CustomerContactsTableBody.constants';
import { getCustomerDisplayName } from './CustomerContactsTableBody.utils';
import { capitalizeAll } from '../../../../../libs/strings';
import ConfirmModal from '../../../../common/ConfirmModal';
import TagsField from '../../../fields/components/TagsField';
import CustomerContactPropType from '../../types/customer';

import './CustomerContactsTableBody.scss';

const propTypes = {
  customers: PropTypes.arrayOf(CustomerContactPropType),
  onUpdateCustomer: PropTypes.func.isRequired,
  onEditButtonClick: PropTypes.func.isRequired,
  onDeleteCustomer: PropTypes.func.isRequired,
  columns: PropTypes.number.isRequired,
  availableTags: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  customers: [],
  availableTags: [],
};

function CustomerContactsTableBody({
  customers,
  onUpdateCustomer,
  onEditButtonClick,
  onDeleteCustomer,
  columns,
  availableTags,
}) {
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  function handleUpdateTags(customerId, data) {
    onUpdateCustomer(customerId, data);
  }

  async function onConfirmDelete() {
    setDeleting(true);
    try {
      await onDeleteCustomer(customerToDelete.id);
      setCustomerToDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  function onCancelDelete() {
    if (!deleting) {
      setCustomerToDelete(null);
    }
  }

  if (_isEmpty(customers)) {
    return (
      <Table.Body>
        <Table.Row>
          <Table.Cell colSpan={columns} textAlign="center">
            {NO_RESULTS_MESSAGE}
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    );
  }

  const customerName = getCustomerDisplayName(customerToDelete);

  return (
    <>
      <Table.Body className="customer-contacts-table-body">
        {customers.map((customer) => (
          <Table.Row key={customer.id}>
            <Table.Cell>{capitalizeAll(customer.first_name)}</Table.Cell>
            <Table.Cell>{capitalizeAll(customer.last_name)}</Table.Cell>
            <Table.Cell>{customer.email}</Table.Cell>
            <Table.Cell>{customer.phone}</Table.Cell>
            <Table.Cell>
              <TagsField
                currentTags={customer.tags}
                availableTags={availableTags}
                onUpdate={(tags) => handleUpdateTags(customer.id, { tags })}
              />
            </Table.Cell>
            <Table.Cell collapsing>
              <div className="action-buttons">
                <Button
                  className="action-button compact"
                  icon="edit"
                  title="Edit"
                  disabled={deleting}
                  onClick={() => {
                    onEditButtonClick(customer);
                  }}
                />
                <Button
                  className="delete-red compact"
                  icon="trash"
                  title="Delete"
                  disabled={deleting}
                  onClick={() => setCustomerToDelete(customer)}
                />
              </div>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
      <ConfirmModal
        open={customerToDelete !== null}
        header="Delete Customer"
        content={`Are you sure you want to delete ${
          customerName || 'this customer'
        }? This action cannot be undone.`}
        confirmButton={
          <Button className="delete-red" content="Delete" loading={deleting} disabled={deleting} />
        }
        cancelButton={
          <Button className="action-button-gray" content="Cancel" disabled={deleting} />
        }
        onCancel={onCancelDelete}
        onConfirm={onConfirmDelete}
      />
    </>
  );
}

CustomerContactsTableBody.propTypes = propTypes;
CustomerContactsTableBody.defaultProps = defaultProps;

export default CustomerContactsTableBody;
